import {noop} from "../../common/utils/functions";
import {resolve} from "../../container";
import {Promises} from "../../common/utils/promises";
import {Timeout} from "../../common/timeout";
import {EopErrorHandler} from "../../common/errorHandler";
import {UnLitElement} from "../../common/elements";
import {customElement, property} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";

export const MINIMUM_SPINNER_DURATION = 500;

export enum SpinnerSize {
    FONT = "font",
    SMALL = "sm",
    MEDIUM = "md",
    LARGE = "lg",
    EXTRA_LARGE = "xl"
}

export enum OverlaySpinnerPosition {
    CENTERED = "centered",
    TOP = "top"
}

export enum SpinnerPosition {
    CENTERED = "centered",
    INLINE = "inline"
}

export interface Spinner {
    showSpinner: () => void;

    hideSpinner: () => void;

    spinWhile: <T>(promise: Promise<T>) => Promise<T>;
}

export abstract class AbstractUnLitSpinner extends UnLitElement implements Spinner {

    @property({
        attribute: "events",
        converter: (value: string | null): string[] => value?.parseCommaSeparated() ?? []
    })
    protected events: string[] = [];

    protected constructor(protected promises: Promises = resolve(Promises)) {
        super();
    }

    public spinner(): Spinner {
        return this;
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.hidden = true;

        const scopedPromises = this.promises.forScope("global");
        for (const event of this.events) {
            scopedPromises.on(event, p => this.spinWhile(p));
        }
    }

    public disconnectedCallback(): void {
        const scopedPromises = this.promises.forScope("global");
        scopedPromises.clear(this.events);
        super.disconnectedCallback();
    }

    public render(): TemplateResult {
        return html`
            <eop-svg-sprite-icon icon-id="spinner" symbol-class="spinner"></eop-svg-sprite-icon>
        `;
    }

    public async spinWhile<T>(promise: Promise<T>): Promise<T> {
        const spinner = this.spinner();
        spinner.showSpinner();
        try {
            return await promise;
        } finally {
            spinner.hideSpinner();
        }
    }

    public showSpinner(): void {
        this.hidden = false;
    }

    public hideSpinner(): void {
        this.hidden = true;
    }
}

@customElement("eop-viewport-spinner")
export class EopViewportSpinner extends AbstractUnLitSpinner {

    private readonly spinnerDecorator: MinimumShowSpinnerDecorator;

    public constructor(promises: Promises = resolve(Promises)) {
        super(promises);
        this.spinnerDecorator = new MinimumShowSpinnerDecorator(this, MINIMUM_SPINNER_DURATION);
    }

    public spinner(): Spinner {
        return this.spinnerDecorator;
    }
}

@customElement("eop-spinner")
export class EopSpinner extends AbstractUnLitSpinner {

    @property({attribute: "size", type: SpinnerSize, reflect: true})
    private size: SpinnerSize = SpinnerSize.LARGE;
    @property({attribute: "position", type: SpinnerPosition, reflect: true})
    private position: SpinnerPosition = SpinnerPosition.CENTERED;
}

@customElement("eop-overlay-spinner")
export class EopOverlaySpinner extends AbstractUnLitSpinner {

    @property({attribute: "size", type: SpinnerSize, reflect: true})
    private size: SpinnerSize = SpinnerSize.LARGE;
    @property({attribute: "position", type: OverlaySpinnerPosition, reflect: true})
    private position: OverlaySpinnerPosition = OverlaySpinnerPosition.CENTERED;

    private readonly spinnerDecorator: MinimumShowSpinnerDecorator;

    public constructor() {
        super();
        this.spinnerDecorator = new MinimumShowSpinnerDecorator(this, MINIMUM_SPINNER_DURATION);
    }

    public spinner(): Spinner {
        return this.spinnerDecorator;
    }
}

export class MinimumShowSpinnerDecorator implements Spinner {
    private minimumShowDurationReached: Promise<void>;

    public constructor(
        public delegate: Spinner,
        public minimumShowDuration: number,
        private timeout: Timeout = resolve(Timeout),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        this.minimumShowDurationReached = Promise.resolve();
    }

    public showSpinner(): void {
        this.delegate.showSpinner();

        this.timeout.cancel(this.minimumShowDurationReached);
        this.minimumShowDurationReached = this.timeout.delay(noop, this.minimumShowDuration);
    }

    public hideSpinner(): void {
        this.minimumShowDurationReached
            .then(() => this.delegate.hideSpinner())
            .catch(e => this.errorHandler.report(e));
    }

    public spinWhile<T>(promise: Promise<T>): Promise<T> {
        return promise.finally(noop);
    }

}